import { useEffect, useState } from 'react';
import { Button } from '../../../modules/design-system';
import {
  createPoolShipping,
  getNewShipping,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
  type PoolShippingService,
} from '../../../modules/api';
import { useShippingFees } from '../../../modules/shipping';
import {
  DeliveryCard,
  DeliveryPageShell,
  SummaryDivider,
  SummaryRow,
} from '../shared';
import { AddressModal } from './AddressModal';
import { AddressPickerModal } from './AddressPickerModal';
import { ItemPickerModal } from './ItemPickerModal';
import { useDeliveryAddresses } from './address/useDeliveryAddresses';
import { YourAddressSection } from './sections/YourAddressSection';
import { ShippingOptionsSection } from './sections/ShippingOptionsSection';
import { ShippingListSection } from './sections/ShippingListSection';

export interface DeliveryNewPageProps {
  client: ApiClient;
  /** Initial pool_items, seeded from the My Packs Cards-tab selection. The
   *  player edits the set in-page via the item picker after that. */
  selectedItems: IframePoolOrderItem[];
  /** Top-bar "< Home" → cancel + return to My Packs. */
  onBack: () => void;
  /** Deprecated — item selection is now an in-page modal; still accepted so
   *  the parent can keep passing it harmlessly. */
  onChooseItems?: () => void;
  /** Fired after `createPoolShipping` succeeds; parent decides whether to
   *  flip to the Deliveries tab, open the new shipping's Show page, etc. */
  onSuccess: (shipping: IframePoolShipping) => void;
  /** Drop the noise-SVG overlay from the page gradient. */
  hideBackgroundPattern?: boolean;
}

/**
 * Delivery New page (Figma 710:3939). Address + Shipping options +
 * Shipping list + Summary + Submit. Address creation goes through
 * `AddressModal`. Services come from `getNewShipping`. Submit posts to
 * `createPoolShipping` and hands the new shipping back via `onSuccess`.
 */
export function DeliveryNewPage({
  client,
  selectedItems,
  onBack,
  onSuccess,
  hideBackgroundPattern = false,
}: DeliveryNewPageProps) {
  const {
    addresses,
    selected: address,
    loading: addressesLoading,
    select: selectAddress,
    addCreated,
  } = useDeliveryAddresses(client);
  // Items in the shipment — seeded from the Cards-tab selection, then edited
  // in-page via the item picker (no navigating away).
  const [items, setItems] = useState<IframePoolOrderItem[]>(selectedItems);
  const [services, setServices] = useState<PoolShippingService[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getNewShipping(client).then((res) => {
      setServices(res.services);
      const first = res.services[0];
      if (first) setSelectedServiceId(first.id);
    });
  }, [client]);

  // Backend-quoted figures: shipping = selected option's full EasyPost price,
  // handling = per-item flat, sales tax = NumeralHQ on (shipping + handling),
  // option_prices = full EasyPost price per option for the radio list. The
  // quote needs items + address, so all stay "TBD" until both exist; re-quotes
  // whenever the address, items or chosen option change.
  const { fees, loading: feesLoading } = useShippingFees(
    client,
    items.map((i) => i.id),
    {
      addressId: address?.id ?? null,
      serviceIds: selectedServiceId ? [selectedServiceId] : [],
    },
  );

  // No items selected → nothing to charge; force every row to "—" regardless
  // of any backend figure (handling in particular must not show a minimum for
  // an empty selection).
  const noItems = items.length === 0;
  const shippingFee = noItems ? null : fees?.shippingFee ?? null;
  const handlingFee = noItems ? null : fees?.handlingFee ?? null;
  const salesTax = noItems ? null : fees?.salesTax ?? null;
  // Total = sum of the charges we actually know; "—" until at least one is
  // quoted, so an all-unknown summary doesn't read as a confident $0.00.
  const knownFees = [shippingFee, handlingFee, salesTax].filter(
    (v): v is number => v !== null,
  );
  const total =
    knownFees.length > 0 ? knownFees.reduce((sum, v) => sum + v, 0) : null;

  const formValid = address !== null && items.length > 0;
  // Block submit while the fee quote is still in flight (and during the POST):
  // submitting against an absent/stale quote risks charging the wrong amount.
  const canSubmit = formValid && !feesLoading && !submitting;
  // Spinner only for an action the user is actually waiting on — the live quote
  // for a valid selection, or the submit POST itself. An invalid/empty form is
  // just disabled, no spinner.
  const submitLoading = submitting || (formValid && feesLoading);

  const handleSubmit = async () => {
    if (!canSubmit || !address) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createPoolShipping(client, {
        addressId: address.id,
        poolItemIds: items.map((i) => i.id),
        poolShippingServiceIds: selectedServiceId ? [selectedServiceId] : [],
      });
      onSuccess(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DeliveryPageShell
      title="Delivery Information"
      onBack={onBack}
      hideBackgroundPattern={hideBackgroundPattern}
    >
      <DeliveryCard>
        <div className="flex flex-col gap-6">
          <YourAddressSection
            address={address}
            loading={addressesLoading}
            onClickAdd={() => setAddressModalOpen(true)}
            onChange={() => setPickerOpen(true)}
          />
          <ShippingOptionsSection
            services={services}
            selectedId={selectedServiceId}
            onSelect={setSelectedServiceId}
            hasItems={items.length > 0}
            hasAddress={address !== null}
            optionPrices={fees?.optionPrices ?? {}}
            loading={feesLoading}
            onChooseItems={() => setItemPickerOpen(true)}
            onChooseAddress={() =>
              addresses.length > 0
                ? setPickerOpen(true)
                : setAddressModalOpen(true)
            }
          />
          <ShippingListSection
            items={items}
            onChooseItems={() => setItemPickerOpen(true)}
          />
        </div>

        <SummaryDivider />

        <div className="flex flex-col gap-3">
          <SummaryRow label="Shipping" value={shippingFee} loading={feesLoading} />
          <SummaryRow label="Handling" value={handlingFee} loading={feesLoading} />
          <SummaryRow label="Sales tax" value={salesTax} loading={feesLoading} />
          <SummaryRow label="Total" value={total} emphasis loading={feesLoading} />
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px]" style={{ color: '#F87171' }}>
            {error}
          </p>
        )}

        <div className="mt-5">
          <Button
            variant={canSubmit ? 'primary' : 'glass-frosted'}
            size="lg"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitLoading}
            fullWidth
          >
            Submit
          </Button>
        </div>
      </DeliveryCard>

      {pickerOpen && (
        <AddressPickerModal
          addresses={addresses}
          selectedId={address?.id ?? null}
          onSelect={(addr) => {
            selectAddress(addr);
            setPickerOpen(false);
          }}
          onAddNew={() => {
            setPickerOpen(false);
            setAddressModalOpen(true);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {addressModalOpen && (
        <AddressModal
          client={client}
          onClose={() => setAddressModalOpen(false)}
          onSuccess={(addr) => {
            addCreated(addr);
            setAddressModalOpen(false);
            setPickerOpen(false);
          }}
        />
      )}

      {itemPickerOpen && (
        <ItemPickerModal
          client={client}
          selectedItems={items}
          onApply={(picked) => {
            setItems(picked);
            setItemPickerOpen(false);
          }}
          onClose={() => setItemPickerOpen(false)}
        />
      )}
    </DeliveryPageShell>
  );
}
